import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  useMediaQuery,
  useTheme as useMuiTheme,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { NavigationMenu } from './NavigationMenu';
import { UserMenu } from './UserMenu';
import { PendingBadge } from '../changes/PendingBadge';
import { NotificationBell } from './NotificationBell';
import { GlobalSearchBar } from '../search/GlobalSearchBar';
import { ThemeSelector } from '../theme/ThemeSelector';

/**
 * AppHeader Component
 *
 * Top navigation header with menu, search, and user controls
 *
 * Features:
 * - Logo/brand with home link
 * - Desktop: Horizontal navigation menu
 * - Mobile: Hamburger menu with drawer
 * - Global search bar (responsive)
 * - Pending changes notification badge (admin only)
 * - User menu dropdown
 * - Sticky/fixed positioning
 *
 * @component
 * @returns {JSX.Element} Application header
 */
export const AppHeader: React.FC = () => {
  const muiTheme = useMuiTheme();
  const { mode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md')); // <960px
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ===== Event Handlers =====

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  // ===== Render =====

  return (
    <>
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 64, sm: 72 },
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open menu"
              onClick={handleMobileMenuToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo / Brand */}
          <Box
            onClick={handleLogoClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mr: 4,
              '&:hover': {
                opacity: 0.8,
              },
              transition: 'opacity 0.2s',
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                fontSize: { xs: '1.1rem', sm: '1.3rem' },
                letterSpacing: '-0.02em',
              }}
            >
              Martz Family
            </Typography>
          </Box>

          {/* Desktop Navigation Menu */}
          {!isMobile && (
            <Box sx={{ flexGrow: 0, mr: 'auto' }}>
              <NavigationMenu />
            </Box>
          )}

          {/* Spacer for mobile */}
          {isMobile && <Box sx={{ flexGrow: 1 }} />}

          {/* Global Search Bar */}
          <Box
            sx={{
              flexGrow: isMobile ? 0 : 1,
              maxWidth: isMobile ? 'auto' : 500,
              mx: isMobile ? 0 : 2,
              display: 'flex',
              justifyContent: isMobile ? 'flex-end' : 'center',
            }}
          >
            <GlobalSearchBar />
          </Box>

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            {/* Theme Selector */}
            <ThemeSelector />

            {/* Theme Toggle */}
            <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
              <IconButton
                color="inherit"
                onClick={toggleTheme}
                aria-label="toggle theme"
                sx={{ mr: 0.5 }}
              >
                {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>

            {/* Pending Changes Badge (Admin Only) */}
            <PendingBadge />

            {/* Notification Bell */}
            <NotificationBell />

            {/* User Menu */}
            <UserMenu />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            width: 280,
            pt: 2,
            px: 2,
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            Menu
          </Typography>
          <IconButton onClick={handleMobileMenuClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Mobile Navigation */}
        <NavigationMenu isMobile onNavigate={handleMobileMenuClose} />
      </Drawer>
    </>
  );
};
